/**
 * Host hydration for @scopmx/markup-editor live-HTML comments.
 *
 * Package guide (setDrawingStorage + loadDrawingState) expects every comment row
 * to come back with `screen` / `htmlScreen` so redirect can rebuild routes.
 *
 * We keep the bundled IndexedDB adapter (drawings stay local) and hydrate by:
 *   1) saveVersion snapshot whose comments include screen + htmlScreenId on shapes
 *   2) merging routesFromHostCommentRows onto the Drawing record before mount
 *
 * That is the IndexedDB equivalent of the HTTP adapter example in the package docs.
 */

import {
    fromPersistedRoutes,
    mergeCommentRoutesInto,
    routesFromHostCommentRows,
} from '@scopmx/markup-editor'

/** Package CommentHtmlScreen shape (hash / trigger / chain / from only). */
export const toPackageCommentScreen = (screen) => {
    if (!screen || typeof screen !== 'object') return null
    const hash =
        typeof screen.hash === 'string' && screen.hash.trim()
            ? screen.hash.trim()
            : undefined
    const trigger =
        screen.trigger && typeof screen.trigger === 'object'
            ? { ...screen.trigger }
            : null
    const chain = (Array.isArray(screen.chain) ? screen.chain : [])
        .filter((step) => step && typeof step === 'object')
        .map((step) => ({ ...step }))
    const from =
        typeof screen.from === 'string' && screen.from.trim()
            ? screen.from.trim()
            : null
    const signature =
        typeof screen.signature === 'string' && screen.signature.trim()
            ? screen.signature.trim()
            : undefined
    // The frozen DOM of the screen. The package declares it on
    // CommentHtmlScreen and consumes it on load, so stripping it here would
    // quietly undo the point of storing it: every other field is a recording of
    // how one reviewer reached the screen, replayed later and hoping the
    // prototype cooperates, while this is the screen itself. Not trimmed — it
    // is opaque encoded transport, not a label.
    const snapshot =
        typeof screen.snapshot === 'string' && screen.snapshot
            ? screen.snapshot
            : undefined
    // A snapshot alone is a complete route back, so it counts as content: a
    // comment carrying nothing else must not be judged empty and dropped.
    if (!hash && !trigger && !chain.length && !from && !signature && !snapshot)
        return null
    return {
        ...(hash ? { hash } : {}),
        ...(trigger ? { trigger } : {}),
        ...(chain.length ? { chain } : {}),
        ...(signature ? { signature } : {}),
        ...(snapshot ? { snapshot } : {}),
        from,
    }
}

/**
 * Flat Comment[] for the editor (roots + replies with parentId).
 * Includes both `screen` and `htmlScreen` aliases the package accepts.
 */
export const toPackageCommentRows = (editorComments) => {
    const rows = []
    ;(Array.isArray(editorComments) ? editorComments : []).forEach((item) => {
        if (!item?.id) return
        const screen = toPackageCommentScreen(item.screen || item.htmlScreen)
        rows.push({
            id: String(item.id),
            annotationId: String(item.annotationId || item.id),
            author: item.author || 'User',
            ...(item.authorId ? { authorId: String(item.authorId) } : {}),
            text: item.text || '',
            createdAt: item.createdAt || new Date().toISOString(),
            ...(item.updatedAt ? { updatedAt: item.updatedAt } : {}),
            ...(item.elementLabel ? { elementLabel: item.elementLabel } : {}),
            ...(screen
                ? {
                      screen,
                      htmlScreen: screen,
                  }
                : {}),
        })
        ;(Array.isArray(item.replies) ? item.replies : []).forEach((reply) => {
            if (!reply?.id) return
            rows.push({
                id: String(reply.id),
                parentId: String(item.id),
                annotationId: String(
                    reply.annotationId || item.annotationId || item.id
                ),
                author: reply.author || 'User',
                ...(reply.authorId ? { authorId: String(reply.authorId) } : {}),
                text: reply.text || '',
                createdAt: reply.createdAt || new Date().toISOString(),
                ...(reply.updatedAt ? { updatedAt: reply.updatedAt } : {}),
            })
        })
    })
    return rows
}

/** Snapshot comments + annotations stamped for package loadDrawingState / revive. */
export const toPackageSnapshot = (snapshot, editorComments) => {
    const comments = toPackageCommentRows(editorComments)
    const hashByAnnotation = new Map()
    comments.forEach((row) => {
        const annotationId = String(row.annotationId || '')
        const hash = String(row.screen?.hash || '')
        if (annotationId && hash.startsWith('html:')) {
            hashByAnnotation.set(annotationId, hash)
        }
    })
    const baseObjects = Array.isArray(snapshot?.annotations?.objects)
        ? snapshot.annotations.objects
        : []
    const objects = baseObjects.map((obj) => {
        const annotationId = String(obj?.annotationId || obj?.id || '')
        const hash = hashByAnnotation.get(annotationId)
        if (!hash) return obj
        return {
            ...obj,
            annotationId: annotationId || obj.annotationId,
            htmlScreenId: hash,
        }
    })
    return {
        ...(snapshot || {}),
        comments,
        annotations: {
            version: snapshot?.annotations?.version || '5',
            objects,
        },
    }
}

/** Prefer explicit trigger; else last chain step (how SPA prototypes navigate). */
const enrichCommentRowsForRoutes = (rows) =>
    rows.map((row) => {
        const screen = row.screen || row.htmlScreen
        if (!screen) return row
        const chain = Array.isArray(screen.chain) ? screen.chain : []
        const trigger =
            screen.trigger && typeof screen.trigger === 'object'
                ? screen.trigger
                : chain.length
                  ? chain[chain.length - 1]
                  : null
        if (!trigger) return row
        const nextScreen = {
            ...screen,
            trigger,
            ...(chain.length ? { chain } : {}),
        }
        return {
            ...row,
            screen: nextScreen,
            htmlScreen: nextScreen,
        }
    })

const chainKey = (chain) =>
    (Array.isArray(chain) ? chain : [])
        .map((step) => `${step?.path || ''}|${step?.text || ''}`)
        .join('>')

/**
 * ALL recorded click paths, keyed by screen id.
 *
 * Read here rather than trusting the round trip through the package: only
 * markup-editor >= the host-click-path release carries chains through
 * `fromPersistedRoutes` / `routesFromHostCommentRows`, and on an older build
 * they are silently dropped — which is exactly the data cross-screen redirect
 * needs on a machine that never recorded the routes itself.
 *
 * Every path is kept, not the longest. Two comments on one screen are two
 * people who each got there their own way, and the routes can be completely
 * different — one walks a wizard, the other arrives in three clicks from
 * sign-in. Collapsing them meant clicking the second comment replayed the
 * first one's journey, and the failure then named a control from a flow the
 * reviewer had never touched.
 */
const chainsByScreenId = (rows) => {
    const chains = new Map()
    ;(Array.isArray(rows) ? rows : []).forEach((row) => {
        const screen = row?.screen || row?.htmlScreen
        const hash = String(screen?.hash || '').trim()
        const chain = Array.isArray(screen?.chain) ? screen.chain : []
        if (!hash || !chain.length) return
        const list = chains.get(hash) || []
        if (list.some((known) => chainKey(known) === chainKey(chain))) return
        list.push(chain)
        chains.set(hash, list)
    })
    return chains
}

/** Union of two chain lists, order preserved, duplicates dropped. */
const mergeChains = (...lists) => {
    const out = []
    const seen = new Set()
    lists.forEach((list) => {
        ;(Array.isArray(list) ? list : []).forEach((chain) => {
            if (!Array.isArray(chain) || !chain.length) return
            const key = chainKey(chain)
            if (seen.has(key)) return
            seen.add(key)
            out.push(chain)
        })
    })
    return out
}

/** Merge host comment.screen rows into a persisted htmlScreenRoutes list. */
/**
 * Chains that no live comment claims any more.
 *
 * Routes are persisted on the drawing record and only ever added to, so a
 * chain outlives the comment that recorded it. Delete the comment and its
 * journey stays in the map — then the next redirect to that screen replays a
 * route belonging to a comment that no longer exists, and the failure names a
 * control from a flow nobody can see any more.
 *
 * Only prunes chains, never whole screens: the trigger and `from` link are
 * still the editor's own observations of the prototype and remain true whether
 * or not anyone commented there.
 */
const pruneOrphanChains = (map, rows) => {
    const live = new Set()
    ;(Array.isArray(rows) ? rows : []).forEach((row) => {
        const screen = row?.screen || row?.htmlScreen
        const chain = Array.isArray(screen?.chain) ? screen.chain : []
        if (chain.length) live.add(chainKey(chain))
    })
    // Nothing to check against — a comment list that failed to load must not be
    // read as "every route is dead".
    if (!live.size) return
    map.forEach((loc) => {
        const kept = mergeChains(loc.chains, loc.chain ? [loc.chain] : []).filter((chain) =>
            live.has(chainKey(chain))
        )
        loc.chains = kept
        loc.chain = kept[0]
        // `verifiedChain` is deliberately untouched. It is not a recording that
        // belongs to a comment — it is the editor's own observation that this
        // path reaches this screen, and that stays true after the comment that
        // prompted the walk is deleted.
    })
}

export const mergeHostCommentRoutes = (existingRoutes, editorComments) => {
    const rows = enrichCommentRowsForRoutes(
        toPackageCommentRows(editorComments)
    )
    const map = fromPersistedRoutes(
        Array.isArray(existingRoutes) ? existingRoutes : []
    )
    pruneOrphanChains(map, rows)
    mergeCommentRoutesInto(map, rows)
    const fromComments = routesFromHostCommentRows(rows)
    const rowChains = chainsByScreenId(rows)
    const byId = new Map()
    map.forEach((loc, screenId) => {
        const chains = mergeChains(
            loc.chains,
            loc.chain ? [loc.chain] : [],
            rowChains.get(screenId)
        )
        byId.set(screenId, {
            screenId,
            hash: loc.hash || '',
            pathname: loc.pathname || '',
            search: loc.search || '',
            ...(loc.trigger ? { trigger: loc.trigger } : {}),
            ...(chains.length ? { chain: chains[0], chains } : {}),
            // The one route observed to work. Worth more than every recording
            // beside it, so it must survive the round trip through storage.
            ...(loc.verifiedChain?.length ? { verifiedChain: loc.verifiedChain } : {}),
            ...(loc.signature ? { signature: loc.signature } : {}),
            from: loc.from ?? null,
        })
    })
    fromComments.forEach((route) => {
        const prev = byId.get(route.screenId)
        const chains = mergeChains(
            prev?.chains,
            prev?.chain ? [prev.chain] : [],
            route.chains,
            route.chain ? [route.chain] : [],
            rowChains.get(route.screenId)
        )
        if (!prev) {
            byId.set(route.screenId, {
                ...route,
                ...(chains.length ? { chain: chains[0], chains } : {}),
            })
            return
        }
        byId.set(route.screenId, {
            ...prev,
            trigger: prev.trigger || route.trigger,
            // Without this the chains were dropped for every screen already in
            // the persisted map — i.e. every screen after the first save — so
            // redirect worked once and then stopped.
            ...(chains.length ? { chain: chains[0], chains } : {}),
            ...(prev.verifiedChain?.length || route.verifiedChain?.length
                ? { verifiedChain: prev.verifiedChain ?? route.verifiedChain }
                : {}),
            ...(prev.signature || route.signature
                ? { signature: prev.signature ?? route.signature }
                : {}),
            from: prev.from ?? route.from ?? null,
        })
    })
    return Array.from(byId.values())
}
